/**
 * useStudyTemplate Hook
 *
 * Manages study template retrieval and teaching mode logic for IXVerify.
 * Handles:
 * - Finding or creating studies by protocol ID
 * - Fetching existing templates
 * - Saving new templates (teaching mode)
 * - Recording usage feedback for accuracy tracking
 */

import { useState, useCallback } from 'react';
import api from '../../services/api';

export interface StudyTemplate {
  id: string;
  site_study_id: string;
  ixrs_template_image?: string;
  vial_template_image?: string;
  ixrs_extraction_prompt?: string;
  vial_extraction_prompt?: string;
  field_hints?: Record<string, string>;
  training_data?: TrainingExample[];
  use_count: number;
  successful_extractions: number;
  manual_corrections: number;
  accuracy_rate?: number;
  created_at: string;
  updated_at: string;
  taught_by?: string;
}

export interface SiteStudy {
  id: string;
  site_id: string;
  protocol_id: string;
  protocol_name?: string;
  sponsor_name?: string;
  is_active: boolean;
  created_at: string;
  created_by?: string;
  study_templates?: StudyTemplate;
  template_status?: 'trained' | 'untrained';
}

export interface TrainingExample {
  protocol_id: string;
  subject_id: string;
  med_id: string;
  vial_id?: string;
  ai_extracted?: {
    protocol_id?: string;
    subject_id?: string;
    med_id?: string;
    vial_id?: string;
  };
  was_corrected: boolean;
  timestamp: string;
}

interface StudyLookupResult {
  study: SiteStudy | null;
  template: StudyTemplate | null;
  is_new: boolean;
  is_teaching_mode: boolean;
}

interface UseStudyTemplateReturn {
  // State
  study: SiteStudy | null;
  template: StudyTemplate | null;
  isTeachingMode: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  findOrCreateStudy: (siteId: string, protocolId: string) => Promise<StudyLookupResult | null>;
  fetchStudyByProtocol: (siteId: string, protocolId: string) => Promise<StudyLookupResult | null>;
  saveTemplate: (studyId: string, templateData: Partial<StudyTemplate>, trainingExample?: TrainingExample) => Promise<StudyTemplate | null>;
  recordFeedback: (studyId: string, wasSuccessful: boolean, wasCorrected: boolean, corrections?: TrainingExample) => Promise<void>;
  resetTemplate: (studyId: string) => Promise<void>;
  clearState: () => void;
}

export function useStudyTemplate(): UseStudyTemplateReturn {
  const [study, setStudy] = useState<SiteStudy | null>(null);
  const [template, setTemplate] = useState<StudyTemplate | null>(null);
  const [isTeachingMode, setIsTeachingMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Find or create a study by protocol ID.
   * Used during verification flow to auto-create studies.
   */
  const findOrCreateStudy = useCallback(async (
    siteId: string,
    protocolId: string
  ): Promise<StudyLookupResult | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post(`/api/sites/${siteId}/studies/find-or-create`, {
        protocol_id: protocolId
      });

      const result = response.data as StudyLookupResult;

      setStudy(result.study);
      setTemplate(result.template);
      setIsTeachingMode(result.is_teaching_mode);

      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to find or create study';
      setError(errorMessage);
      console.error('Error in findOrCreateStudy:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch study and template by protocol ID (read-only lookup).
   */
  const fetchStudyByProtocol = useCallback(async (
    siteId: string,
    protocolId: string
  ): Promise<StudyLookupResult | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/sites/${siteId}/studies/by-protocol/${encodeURIComponent(protocolId)}`);

      const result = response.data as StudyLookupResult;

      setStudy(result.study);
      setTemplate(result.template);
      setIsTeachingMode(result.is_teaching_mode);

      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch study';
      setError(errorMessage);
      console.error('Error in fetchStudyByProtocol:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Save or update a template (used in teaching mode).
   */
  const saveTemplate = useCallback(async (
    studyId: string,
    templateData: Partial<StudyTemplate>,
    trainingExample?: TrainingExample
  ): Promise<StudyTemplate | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post(`/api/studies/${studyId}/template`, {
        ...templateData,
        training_example: trainingExample
      });

      const savedTemplate = response.data as StudyTemplate;
      setTemplate(savedTemplate);
      setIsTeachingMode(false); // No longer in teaching mode after saving template

      return savedTemplate;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save template';
      setError(errorMessage);
      console.error('Error in saveTemplate:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Record usage feedback for accuracy tracking.
   */
  const recordFeedback = useCallback(async (
    studyId: string,
    wasSuccessful: boolean,
    wasCorrected: boolean,
    corrections?: TrainingExample
  ): Promise<void> => {
    try {
      await api.post(`/api/studies/${studyId}/template/feedback`, {
        was_successful: wasSuccessful,
        was_corrected: wasCorrected,
        corrections
      });

      // Update local template state with new stats
      if (template) {
        setTemplate({
          ...template,
          use_count: template.use_count + 1,
          successful_extractions: template.successful_extractions + (wasSuccessful ? 1 : 0),
          manual_corrections: template.manual_corrections + (wasCorrected ? 1 : 0)
        });
      }
    } catch (err: any) {
      console.error('Error recording feedback:', err);
      // Don't throw - feedback recording is not critical
    }
  }, [template]);

  /**
   * Reset/delete a template (for re-teaching).
   */
  const resetTemplate = useCallback(async (studyId: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await api.delete(`/api/studies/${studyId}/template`);
      setTemplate(null);
      setIsTeachingMode(true);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to reset template';
      setError(errorMessage);
      console.error('Error in resetTemplate:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear all state (for starting a new verification).
   */
  const clearState = useCallback(() => {
    setStudy(null);
    setTemplate(null);
    setIsTeachingMode(false);
    setError(null);
  }, []);

  return {
    study,
    template,
    isTeachingMode,
    isLoading,
    error,
    findOrCreateStudy,
    fetchStudyByProtocol,
    saveTemplate,
    recordFeedback,
    resetTemplate,
    clearState
  };
}

/**
 * Generate an optimized extraction prompt based on training examples.
 * This builds a custom prompt that includes examples from previous corrections.
 */
export function generateOptimizedPrompt(
  basePrompt: string,
  trainingExamples: TrainingExample[],
  fieldType: 'protocol_id' | 'subject_id' | 'med_id' | 'vial_id'
): string {
  if (!trainingExamples || trainingExamples.length === 0) {
    return basePrompt;
  }

  // Get the most recent examples (up to 3)
  const recentExamples = trainingExamples
    .filter(ex => ex[fieldType])
    .slice(-3);

  if (recentExamples.length === 0) {
    return basePrompt;
  }

  // Build example string
  const exampleValues = recentExamples.map(ex => ex[fieldType]).join(', ');

  return `${basePrompt}

Previous verified values for this protocol: ${exampleValues}
Look for values matching this format.`;
}
